/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/FeaturedSectors/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import styles from "./index.module.css";

import { IconButton, CircularProgress, Backdrop, SvgIcon } from "@mui/material";
import { ReactComponent as SearchIcon } from "../../assets/icons/Search.svg";
import { ReactComponent as MedalIcon } from "../../assets/icons/FeaturedSectors/Medal.svg";
import defaultSectorImage from "../../assets/icons/FeaturedSectors/Machinery.jpg";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { getFeaturedSectors } from "../../logic/featuredSectors";
import { generateAssetUrl } from "../../utils/common.js";
import { useTranslation } from "react-i18next";
import AssetImage from "../AssetImage";

const getSectorImageUrl = (sector) => {
  if (sector.sector != null && sector.sector !== "2") {
    return generateAssetUrl(`requirement/sectorImages/${sector.sector}.png`);
  }

  return defaultSectorImage;
};


const FeaturedSectors = ({ handleSearchAgain }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [featuredSectors, setFeaturedSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isTablet = window.innerWidth <= 991;
  const isMediumMobile = window.innerWidth <= 768;
  const isSmallMobile = window.innerWidth <= 480;
  const isNotComputer = isTablet || isMediumMobile || isSmallMobile;

  useEffect(() => {
    fetchSectors();
  }, []);

  const fetchSectors = async () => {
    try {
      setLoading(true);
      setError(null);
      const sectors = await getFeaturedSectors(navigate);
      logger.info("Sectors: ", { data: sectors });
      setFeaturedSectors(Array.isArray(sectors) ? sectors : []);
    } catch (error) {
      logger.error("Error fetching featured sectors:", {
        err: sanitizeError(error),
      });
      setError(t("components.featuredSections.errorLoadingSectors"));
      setFeaturedSectors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSectorSearch = (sector) => {
    logger.info("Sector: ", { data: sector });
    logger.info("Sector name: ", { data: sector.nombre.trim() });
    handleSearchAgain({ sector: sector.sector });
  };

  if (loading) {
    return (
      <div className={styles.featuredSection}>
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          }}
          open={loading}
        >
          <CircularProgress
            size={60}
            thickness={4}
            sx={{
              color: "#4eabe7",
            }}
          />
        </Backdrop>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.featuredSection}>
        <div className={styles.errorMessage}>{error}</div>
      </div>
    );
  }

  return (
    <div className={styles.featuredSection}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <MedalIcon className={styles.titleIcon} />
          <h2 className={styles.titleText}>
            {t("components.featuredSectors.header")}
          </h2>
        </div>
        <span className={styles.subtitle}>
          {t("components.featuredSectors.subtitle")}
        </span>
        <hr className={styles.divider} />
      </div>
        {featuredSectors.length == 0 && (
          <div className={styles.noResults}>
            <p>{t("components.featuredSectors.noResults")}</p>
          </div>
        )}
      <div className={styles.sectorsList}>
        {featuredSectors.length > 0 &&
          featuredSectors.map((sector, index) => (
            <div key={index} className={styles.sectorCard}>
              <div className={styles.sectorImage}>
                <AssetImage
                  src={getSectorImageUrl(sector)}
                  alt={sector.nombre}
                  className={styles.sectorImageSrc}
                  onError={(event) => {
                    const img = event.currentTarget;
                    if (img.dataset.fallbackApplied) {
                      return;
                    }

                    img.dataset.fallbackApplied = "true";
                    img.src = defaultSectorImage;
                  }}
                />
                {/* <div className={styles.imageOverlay} /> */}
              </div>
              <div className={styles.sectorInfo}>
                <div className={styles.sectorHeader}>
                  <span className={styles.sectorTitle}>
                    {t("components.featuredSectors.sector")}
                  </span>
                  <span className={styles.sectorName}>
                    {sector.nombre}
                  </span>
                </div>
                <div className={styles.iconContainer} onClick={() => handleSectorSearch(sector)}>
                  <SvgIcon
                    className={styles.icon}
                    component={SearchIcon}
                  />
                </div>
              </div>
            </div>
          ))}

      </div>

    </div>
  );
};

export default FeaturedSectors;
